import { formatMs, pingSummary } from "./ping.ts";
import { say } from "../../words.ts";

/**
 * `/ping`'s result as a panel (ADR-068's amendment "`/ping` as a network panel"): a header counting the socket's
 * replies, then three measured lines, Network (the probes' round trips from Resource Timing), Socket (the `pong`s'
 * round trips as the game sees them) and Game response (the Socket average less the Network average, never below
 * 0, in whole milliseconds). The panel is plain numbers, kept in the log's line; the log words them as it draws,
 * through the functions here, and a screen reader hears them as one sentence built from the same numbers.
 */

/**
 * `/ping`'s round trips: the socket's, each `ping` to its `pong` on the page's clock, and the network's, from the
 * browser's Resource Timing, each in the order it came back.
 */
export interface PingSamples {
  readonly socket: readonly number[];
  readonly network: readonly number[];
}

/**
 * One measure's numbers in milliseconds, unrounded.
 */
export interface PingMeasure {
  readonly averageMs: number;
  readonly minMs: number;
  readonly maxMs: number;
}

/**
 * The panel's numbers: how many pings went, how many `pong`s came back, the network's and the socket's measures
 * (null for one with no sample) and Game response in whole milliseconds (null unless both measures have one).
 */
export interface PingPanel {
  readonly sent: number;
  readonly replies: number;
  readonly network: PingMeasure | null;
  readonly socket: PingMeasure | null;
  readonly gameMs: number | null;
}

/**
 * One measured line of the panel as the log draws it: its key, its label, its value ("8.3 ms" or "not available"),
 * its range ("(min 1.0, max 20)", or empty), its meaning, the tooltip and the words `/help ping` will say, and
 * whether it has a measure (false where the value reads "not available").
 */
export interface PingPanelRow {
  readonly key: "network" | "socket" | "game";
  readonly label: string;
  readonly value: string;
  readonly range: string;
  readonly meaning: string;
  readonly available: boolean;
}

/**
 * A measure's numbers, or null for no sample.
 *
 * @param samples The round trips in milliseconds.
 * @returns The measure.
 */
function measure(samples: readonly number[]): PingMeasure | null {
  if (samples.length === 0) {
    return null;
  }
  const { averageMs, minMs, maxMs } = pingSummary(samples);
  return { averageMs, minMs, maxMs };
}

/**
 * The panel for a run's round trips.
 *
 * @param samples The socket's and the network's round trips in milliseconds.
 * @param sent How many pings went.
 * @returns The panel, or null when nothing came back at all, which the run shows as its error line.
 * @example
 * pingPanel({ socket: [9, 9], network: [0.4, 0.4] }, 5);
 * // { sent: 5, replies: 2, network: { averageMs: 0.4, ... }, socket: { averageMs: 9, ... }, gameMs: 9 }
 */
export function pingPanel(samples: PingSamples, sent: number): PingPanel | null {
  const network = measure(samples.network);
  const socket = measure(samples.socket);
  if (network === null && socket === null) {
    return null;
  }
  const gameMs = network === null || socket === null ? null : Math.round(Math.max(0, socket.averageMs - network.averageMs));
  return { sent, replies: samples.socket.length, network, socket, gameMs };
}

/**
 * The panel's header.
 *
 * @param panel The panel.
 * @returns The header, a label.
 * @example
 * pingPanelHeader(panel);
 * // "Ping · 5 of 5 replies"
 */
export function pingPanelHeader(panel: PingPanel): string {
  return say("chat.pingHeader", { title: { k: "chat.pingTitle" }, replies: say("chat.pingReplies", { replies: String(panel.replies), sent: String(panel.sent) }) });
}

/**
 * A time with its unit, as the panel shows it.
 *
 * @param ms The time in milliseconds.
 * @returns The time, "8.3 ms".
 */
function withUnit(ms: number): string {
  return say("chat.pingMs", { value: formatMs(ms) });
}

/**
 * A measured line's value and range.
 *
 * @param numbers The measure, or null for none.
 * @returns Its average with the unit, its range and that it is available, or "not available" and no range.
 */
function measured(numbers: PingMeasure | null): { readonly value: string; readonly range: string; readonly available: boolean } {
  if (numbers === null) {
    return { value: say("chat.notAvailable"), range: "", available: false };
  }
  return { value: withUnit(numbers.averageMs), range: say("chat.pingRange", { min: formatMs(numbers.minMs), max: formatMs(numbers.maxMs) }), available: true };
}

/**
 * The panel's three measured lines, in the order they show.
 *
 * @param panel The panel.
 * @returns Network, Socket and Game response.
 */
export function pingPanelRows(panel: PingPanel): readonly PingPanelRow[] {
  return [
    { key: "network", label: say("chat.pingNetwork"), ...measured(panel.network), meaning: say("chat.pingNetworkMeaning") },
    { key: "socket", label: say("chat.pingSocket"), ...measured(panel.socket), meaning: say("chat.pingSocketMeaning") },
    { key: "game", label: say("chat.pingGame"), value: panel.gameMs === null ? say("chat.notAvailable") : say("chat.pingMs", { value: String(panel.gameMs) }), range: "", meaning: say("chat.pingGameMeaning"), available: panel.gameMs !== null },
  ];
}

/**
 * One measure as a screen reader hears it.
 *
 * @param label The line's label.
 * @param numbers The measure, or null for none.
 * @returns The sentence.
 */
function spokenMeasure(label: string, numbers: PingMeasure | null): string {
  if (numbers === null) {
    return say("chat.pingSpokenValue", { label, value: { k: "chat.notAvailable" } });
  }
  return say("chat.pingSpokenMeasure", { label, value: withUnit(numbers.averageMs), min: formatMs(numbers.minMs), max: formatMs(numbers.maxMs) });
}

/**
 * The panel as one message for assistive technology, in sentences, from the same numbers the grid shows.
 *
 * @param panel The panel.
 * @returns The message.
 * @example
 * pingPanelSpoken(panel);
 * // "Ping, 5 of 5 replies. Network 8.3 ms average, min 1.0, max 20. Socket 10 ms average, min 3.1, max 22. Game response 2 ms."
 */
export function pingPanelSpoken(panel: PingPanel): string {
  const game = panel.gameMs === null ? say("chat.notAvailable") : say("chat.pingMs", { value: String(panel.gameMs) });
  return [
    say("chat.pingSpokenTitle", { title: { k: "chat.pingTitle" }, replies: say("chat.pingReplies", { replies: String(panel.replies), sent: String(panel.sent) }) }),
    spokenMeasure(say("chat.pingNetwork"), panel.network),
    spokenMeasure(say("chat.pingSocket"), panel.socket),
    say("chat.pingSpokenValue", { label: say("chat.pingGame"), value: game }),
  ].join(" ");
}
